import { ApiProblemError } from '@taler/api-client'
import type { CurrencyCode } from '@taler/domain'
import { ACCOUNT_ICON_NAMES, type AccountIconName } from '@taler/ui'

/** Mirrors CreateAccountRequest's accountClass enum (contract/openapi.json). */
export const ACCOUNT_CLASSES = ['ASSET', 'LIABILITY', 'INCOME', 'EXPENSE', 'EQUITY'] as const
type AccountClassName = (typeof ACCOUNT_CLASSES)[number]

/** Mirrors CreateAccountRequest's type enum (contract/openapi.json). */
export const ACCOUNT_TYPES = [
  'CASH',
  'SAVINGS',
  'STOCKS',
  'CRYPTO',
  'BROKER_CASH',
  'FX',
  'EMERGENCY_FUND',
  'CONTACT',
  'LOAN',
  'INTEREST',
  'FEE',
  'OPENING',
  'RECONCILIATION',
  'PNL',
  'OTHER',
] as const
type AccountTypeName = (typeof ACCOUNT_TYPES)[number]

/** The offered set - drawn by AccountIcon.vue, so every value here is guaranteed to render. */
export const ACCOUNT_ICONS = ACCOUNT_ICON_NAMES

export const DEFAULT_ACCOUNT_ICON: AccountIconName = 'CASH'
export const DEFAULT_ACCOUNT_COLOR = '#3b82f6'

/** Eighteen swatches offered alongside the native colour input - `accounts.form.presetColors`. */
export const ACCOUNT_COLOR_PRESETS = [
  '#3b82f6',
  '#ef4444',
  '#f97316',
  '#f59e0b',
  '#eab308',
  '#84cc16',
  '#22c55e',
  '#14b8a6',
  '#06b6d4',
  '#0ea5e9',
  '#6366f1',
  '#8b5cf6',
  '#a855f7',
  '#d946ef',
  '#ec4899',
  '#f43f5e',
  '#64748b',
  '#78716c',
] as const

export interface CreateAccountInput {
  readonly code: string
  readonly accountClass: (typeof ACCOUNT_CLASSES)[number]
  readonly type: (typeof ACCOUNT_TYPES)[number]
  readonly names: Readonly<Partial<Record<'en' | 'hu', string>>>
  readonly currency: CurrencyCode | undefined
  readonly parentId: string | undefined
  /** Parent-only asset or liability account; the server takes no currency for one. */
  readonly isGroup: boolean
  readonly icon: AccountIconName
  readonly color: string
}

export interface UpdateAccountInput {
  readonly id: string
  readonly nameEn: string
  readonly nameHu: string
  readonly icon: AccountIconName
  readonly color: string
}

export type CreateAccountErrorField = 'code' | 'names' | 'currency' | 'general'

export interface CreateAccountError {
  readonly field: CreateAccountErrorField
  /** The server's own explanation, shown when the field alone does not say enough. */
  readonly detail: string | undefined
  /** The server's error code, when it sent one - the key to a friendly message. */
  readonly code: string | undefined
}

/** Mirrors the backend's accounts_class_type constraint; CONTACT is provisioned by contacts, never offered here. */
export const LEGAL_TYPES: Readonly<Record<AccountClassName, readonly AccountTypeName[]>> = {
  ASSET: ['CASH', 'SAVINGS', 'STOCKS', 'CRYPTO', 'BROKER_CASH', 'FX', 'EMERGENCY_FUND', 'OTHER'],
  LIABILITY: ['LOAN', 'OTHER'],
  INCOME: ['INTEREST', 'OTHER'],
  EXPENSE: ['FEE', 'OTHER'],
  EQUITY: ['OPENING', 'RECONCILIATION', 'PNL', 'OTHER'],
}

/** Assets need a currency, liabilities may have one, every other class must have none. */
export function currencyRule(accountClass: AccountClassName): 'required' | 'optional' | 'none' {
  if (accountClass === 'ASSET') return 'required'
  return accountClass === 'LIABILITY' ? 'optional' : 'none'
}

export const ACCOUNT_KINDS = [
  { id: 'bank', accountClass: 'ASSET', type: 'OTHER', icon: 'BANK', parentCode: 'ASSET.BANK' },
  { id: 'cash', accountClass: 'ASSET', type: 'CASH', icon: 'CASH', parentCode: 'ASSET.CASH' },
  { id: 'savings', accountClass: 'ASSET', type: 'SAVINGS', icon: 'SAVINGS', parentCode: 'ASSET.SAVINGS' },
  {
    id: 'investments',
    accountClass: 'ASSET',
    type: 'STOCKS',
    icon: 'INVESTMENT',
    parentCode: 'ASSET.INVEST',
  },
  { id: 'crypto', accountClass: 'ASSET', type: 'CRYPTO', icon: 'CRYPTO', parentCode: 'ASSET.CRYPTO' },
  { id: 'creditCard', accountClass: 'LIABILITY', type: 'OTHER', icon: 'CARD', parentCode: 'LIABILITY.CARD' },
  { id: 'expense', accountClass: 'EXPENSE', type: 'OTHER', icon: 'SHOPPING', parentCode: 'EXPENSE' },
  { id: 'income', accountClass: 'INCOME', type: 'OTHER', icon: 'CHART', parentCode: 'INCOME' },
] as const satisfies readonly {
  id: string
  accountClass: AccountClassName
  type: AccountTypeName
  icon: AccountIconName
  parentCode: string
}[]

export type AccountKind = (typeof ACCOUNT_KINDS)[number]

const MAX_CODE_SLUG = 40

/** A child's code must extend its parent's; `attempt` > 0 disambiguates after the server reports the code taken. */
export function generateAccountCode(parentCode: string, name: string, attempt = 0): string {
  const slug = name
    .normalize('NFD')
    .replace(/\p{M}/gu, '')
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, MAX_CODE_SLUG)
  return `${parentCode}.${slug === '' ? 'ACCOUNT' : slug}${attempt > 0 ? `_${attempt + 1}` : ''}`
}

/** The server is the authority on every check; this maps its rejection back to the field. */
export function classifyCreateAccountError(error: unknown): CreateAccountError {
  if (!(error instanceof ApiProblemError)) return { field: 'general', detail: undefined, code: undefined }
  const { detail } = error.problem
  const code = error.code
  const field = FIELD_BY_CODE[code] ?? 'general'
  return { field, detail, code }
}

const FIELD_BY_CODE: Readonly<Record<string, CreateAccountErrorField>> = {
  'account.code_already_exists': 'code',
  'account.name_required': 'names',
  'account.currency_required': 'currency',
  'account.currency_not_allowed': 'currency',
}

/** Codes the dialog explains in its own words (`accounts.errors.<code>`). */
export const FRIENDLY_CREATE_ERRORS = [
  'account.parent_required',
  'account.parent_class_mismatch',
  'account.parent_not_allowed',
  'account.parent_archived',
  'account.parent_not_found',
  'account.group_class_not_allowed',
  'account.code_must_extend_parent',
  'account.type_not_allowed_for_class',
  'account.currency_not_supported',
] as const
